import React, { useEffect, useRef, useState } from 'react';
import { uploadAPI } from '../services/api';

const UploadPanel = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState('idle');
  const [jobDetails, setJobDetails] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const clearPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearPolling();
  }, []);

  const startPolling = (currentJobId) => {
    clearPolling();
    pollRef.current = setInterval(async () => {
      try {
        const response = await uploadAPI.getCSVUploadStatus(currentJobId);
        const status = response.data?.status || 'processing';
        setJobStatus(status);
        setJobDetails(response.data?.details || null);

        if (status === 'completed' || status === 'failed') {
          if (status === 'failed') {
            setError(response.data?.error || 'CSV processing failed.');
          }
          clearPolling();
        }
      } catch (pollError) {
        setError(pollError.response?.data?.detail || 'Failed to check upload status.');
        clearPolling();
      }
    }, 2000);
  };

  const handleUpload = async () => {
    if (!selectedFile || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    setJobDetails(null);
    setJobStatus('uploading');

    try {
      const response = await uploadAPI.uploadCSV(selectedFile);
      const currentJobId = response.data?.job_id;

      if (!currentJobId) {
        throw new Error('No job ID returned from upload endpoint.');
      }

      setJobId(currentJobId);
      setJobStatus('processing');
      startPolling(currentJobId);
    } catch (uploadError) {
      setJobStatus('failed');
      setError(uploadError.response?.data?.detail || uploadError.message || 'Upload failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusLabelMap = {
    idle: 'Ready',
    uploading: 'Uploading',
    queued: 'Uploading',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mt-4">
      <h2 className="text-lg font-semibold mb-3">CSV Upload</h2>
      <div className="space-y-3">
        <input
          type="file"
          accept=".csv"
          onChange={(event) => {
            const file = event.target.files?.[0] || null;
            setSelectedFile(file);
            setError('');
          }}
          className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
        />

        <button
          onClick={handleUpload}
          disabled={!selectedFile || isSubmitting || jobStatus === 'processing'}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Uploading...' : 'Upload'}
        </button>

        <div className="text-sm">
          <span className="text-gray-600">Status: </span>
          <span className={`font-medium ${jobStatus === 'failed' ? 'text-red-600' : jobStatus === 'completed' ? 'text-green-600' : 'text-gray-900'}`}>
            {statusLabelMap[jobStatus] || 'Processing'}
          </span>
        </div>

        {jobId && (
          <div className="text-xs text-gray-500 break-all">Job ID: {jobId}</div>
        )}

        {jobStatus === 'completed' && jobDetails && (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
            Imported {jobDetails.processed || 0} sites; skipped {jobDetails.skipped || 0}; indexed {jobDetails.indexed_sites || 0} sites.
          </div>
        )}

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>
        )}
      </div>
    </div>
  );
};

export default UploadPanel;
